import React from 'react';
import { useTranslation } from 'react-i18next';
import { useBooking } from '../../contexts/BookingContext';
import type { Facility } from '../../types';
import type { ViewMode } from '../SchedulerView';

// Category-slug → emoji map.
// Multiple aliases per sport (e.g. `soccer` + `football`) so we cover whichever
// slug the backend uses without further coordination. Unmapped slugs fall back
// to 🏟️ (stadium — neutral, semantic for "facility").
const facilityIcons: Record<string, string> = {
  // Ball sports — racket
  tennis: '🎾',
  padel: '🎾',
  squash: '🥎',
  badminton: '🏸',
  'table-tennis': '🏓',
  pingpong: '🏓',
  // Ball sports — team
  basketball: '🏀',
  soccer: '⚽',
  football: '⚽',
  volleyball: '🏐',
  handball: '🤾',
  rugby: '🏉',
  hockey: '🏒',
  // Water
  swimming: '🏊',
  pool: '🏊',
  diving: '🤿',
  // Precision
  shooting: '🎯',
  'shooting-range': '🎯',
  archery: '🏹',
  bowling: '🎳',
  golf: '⛳',
  // Strength / movement
  gym: '🏋️',
  fitness: '🏋️',
  yoga: '🧘',
  boxing: '🥊',
  'martial-arts': '🥋',
  mma: '🥋',
  climbing: '🧗',
  trampoline: '🤸',
  dance: '💃',
  dancing: '💃',
  // Ice / snow / wheels
  'ice-rink': '⛸️',
  skating: '⛸️',
  skiing: '🎿',
  rollerblading: '🛼',
  cycling: '🚴',
  // Outdoor / track
  running: '🏃',
  track: '🏃',
  'horse-riding': '🐎',
  equestrian: '🐎',
  // Generic
  other: '🏟️',
};

const FALLBACK_ICON = '🏟️';

const getFacilityIcon = (type: string | undefined): string => {
  if (!type) return FALLBACK_ICON;
  const normalized = type.toLowerCase().replace(/_/g, '-');
  if (facilityIcons[normalized]) return facilityIcons[normalized];
  // Fall back to head of compound slugs: `shooting-range` → `shooting`, `tennis-court` → `tennis`.
  const head = normalized.split('-')[0];
  return facilityIcons[head] || FALLBACK_ICON;
};

interface FacilityListProps {
  viewMode: ViewMode;
  setViewMode: (mode: ViewMode) => void;
  hiddenFacilityIds: Set<string>;
  onToggleFacilityVisibility: (id: string) => void;
}

export const FacilityList: React.FC<FacilityListProps> = ({
  viewMode,
  setViewMode,
  hiddenFacilityIds,
  onToggleFacilityVisibility,
}) => {
  const { t } = useTranslation();
  const { facilities, selectedFacility, setSelectedFacility, isLoading, error, refreshBusinessProfile } = useBooking();

  const handleClickFacility = (facility: Facility) => {
    if (viewMode === 'single') {
      setSelectedFacility(facility);
    } else {
      onToggleFacilityVisibility(facility.id);
    }
  };

  const isActive = (facility: Facility): boolean => {
    if (viewMode === 'single') {
      return selectedFacility?.id === facility.id;
    }
    return !hiddenFacilityIds.has(facility.id);
  };

  const visibleCount =
    viewMode === 'all'
      ? facilities.filter(f => !hiddenFacilityIds.has(f.id)).length
      : facilities.length;

  // Przy dużej liczbie osi (np. 12 stanowisk) lista musi pomieścić wszystkie
  // obiekty bez przewijania, więc wiersze są węższe i jednolinijkowe — w obu
  // widokach (pojedynczym i grupowym). Obiekty znają swoje osie, więc drugi
  // opis (facility.description) jest zbędny.

  return (
    <div className="w-60 shrink-0 bg-surface-card border-r border-border-subtle flex flex-col h-full">
      <div className="px-4 pt-4 pb-3 border-b border-border-subtle">
        <div className="flex gap-1 p-1 bg-surface-app-elevated rounded-lg">
          <button
            onClick={() => setViewMode('single')}
            className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'single'
                ? 'bg-brand-primary text-white shadow-sm'
                : 'text-ink-secondary hover:bg-surface-card hover:text-ink-primary'
            }`}
          >
            {t('viewToggle.singleFacility')}
          </button>
          <button
            onClick={() => setViewMode('all')}
            className={`flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'all'
                ? 'bg-brand-primary text-white shadow-sm'
                : 'text-ink-secondary hover:bg-surface-card hover:text-ink-primary'
            }`}
          >
            {t('viewToggle.allFacilities')}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto"></div>
              <p className="text-ink-secondary mt-4">{t('facilities.loadingList')}</p>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-4">
              <p className="text-brand-warm-deep mb-4">{error}</p>
              <button
                onClick={refreshBusinessProfile}
                className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:bg-brand-primary-hover transition-colors"
              >
                {t('common.retry')}
              </button>
            </div>
          </div>
        ) : facilities.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-ink-secondary text-center">{t('facilities.noFacilitiesAvailable')}</p>
          </div>
        ) : (
          <div className="space-y-1">
            {facilities.map((facility) => {
              const active = isActive(facility);
              const inactiveClass =
                viewMode === 'all'
                  ? 'bg-surface-app-elevated text-ink-muted opacity-60 hover:opacity-100 hover:bg-surface-card-hover'
                  : 'bg-surface-app-elevated text-ink-primary hover:bg-surface-card-hover';
              return (
                <button
                  key={facility.id}
                  onClick={() => handleClickFacility(facility)}
                  className={`w-full text-left rounded-lg px-3 py-2 transition-all duration-200 ${
                    active
                      ? 'bg-brand-primary text-white shadow-lg shadow-brand-primary/20'
                      : inactiveClass
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <span className="text-lg leading-none">
                      {getFacilityIcon(facility.type)}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm truncate">
                        {facility.name}
                      </h3>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="p-4 border-t border-border-subtle">
        <div className="bg-surface-app-elevated rounded-lg p-3">
          <div className="flex items-center justify-between text-sm">
            <span className="text-ink-secondary">{t('facilities.totalLabel')}</span>
            <span className="text-ink-primary font-semibold">
              {viewMode === 'all' ? `${visibleCount}/${facilities.length}` : facilities.length}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
